import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Calendar, BarChart2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_BG = [
  'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни',
  'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
];

export default function AttendancePage({ classes, token }) {
  const [view, setView] = useState('mark');

  // Mark attendance
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);

  // Report
  const [reportClass, setReportClass] = useState('');
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch students when class changes
  useEffect(() => {
    if (!selectedClass) { setStudents([]); setAttendance({}); return; }
    setLoadingStudents(true);
    axios.get(`${API_URL}/classes/${selectedClass}/students`, { headers })
      .then(res => {
        setStudents(res.data);
        const init = {};
        res.data.forEach(s => { init[s.user_id] = 'present'; });
        setAttendance(init);
      })
      .catch(() => toast.error('Грешка при зареждане на ученици'))
      .finally(() => setLoadingStudents(false));
  }, [selectedClass]); // eslint-disable-line react-hooks/exhaustive-deps

  // Overlay existing attendance when class+date changes
  useEffect(() => {
    if (!selectedClass || !selectedDate) return;
    axios.get(`${API_URL}/attendance`, { headers, params: { class_id: selectedClass, date: selectedDate } })
      .then(res => {
        if (res.data.length > 0) {
          const existing = {};
          res.data.forEach(r => { existing[r.student_id] = r.status; });
          setAttendance(prev => ({ ...prev, ...existing }));
        }
      })
      .catch(() => {});
  }, [selectedClass, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (studentId) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: prev[studentId] === 'present' ? 'absent' : 'present'
    }));
  };

  const handleSave = async () => {
    if (!selectedClass || !selectedDate || students.length === 0) {
      toast.error('Изберете клас и дата');
      return;
    }
    setSaving(true);
    try {
      const records = students.map(s => ({
        student_id: s.user_id,
        status: attendance[s.user_id] || 'present'
      }));
      await axios.post(`${API_URL}/attendance/bulk`, {
        class_id: selectedClass,
        date: selectedDate,
        records
      }, { headers });
      toast.success('Присъствията са записани успешно');
    } catch {
      toast.error('Грешка при записване');
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    if (!reportClass) { toast.error('Изберете клас'); return; }
    setLoadingReport(true);
    try {
      const res = await axios.get(`${API_URL}/attendance/report`, {
        headers,
        params: { class_id: reportClass, month: reportMonth, year: reportYear }
      });
      setReportData(res.data);
    } catch {
      toast.error('Грешка при зареждане на справката');
    } finally {
      setLoadingReport(false);
    }
  };

  const presentCount = Object.values(attendance).filter(v => v === 'present').length;
  const absentCount = Object.values(attendance).filter(v => v === 'absent').length;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-900">Присъствия</h2>

      {/* View toggle */}
      <div className="flex gap-2 flex-wrap">
        <Button
          data-testid="attendance-mark-btn"
          variant={view === 'mark' ? 'default' : 'outline'}
          onClick={() => setView('mark')}
          className={view === 'mark' ? 'bg-[#00966E] hover:bg-[#007a59] text-white' : ''}
        >
          <Calendar className="h-4 w-4 mr-2" />
          Отбелязване
        </Button>
        <Button
          data-testid="attendance-report-btn"
          variant={view === 'report' ? 'default' : 'outline'}
          onClick={() => setView('report')}
          className={view === 'report' ? 'bg-[#00966E] hover:bg-[#007a59] text-white' : ''}
        >
          <BarChart2 className="h-4 w-4 mr-2" />
          Месечна справка
        </Button>
      </div>

      {/* MARK ATTENDANCE */}
      {view === 'mark' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Клас</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass} data-testid="attendance-class-select">
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Изберете клас..." />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="att-date">Дата</Label>
              <input
                id="att-date"
                data-testid="attendance-date-input"
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00966E]"
              />
            </div>
          </div>

          {loadingStudents && (
            <p className="text-slate-500 text-sm">Зареждане на ученици...</p>
          )}

          {!loadingStudents && selectedClass && students.length === 0 && (
            <p className="text-slate-400 text-sm">Няма записани ученици в този клас.</p>
          )}

          {!loadingStudents && students.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">
                  {selectedDate} &mdash; {classes.find(c => c.class_id === selectedClass)?.name}
                </span>
                <div className="flex gap-3 text-sm">
                  <span className="text-green-600 font-medium">П: {presentCount}</span>
                  <span className="text-red-600 font-medium">О: {absentCount}</span>
                </div>
              </div>
              <div className="divide-y">
                {students.map(student => {
                  const status = attendance[student.user_id] || 'present';
                  return (
                    <div key={student.user_id} className="flex items-center justify-between py-2.5">
                      <span className="font-medium text-slate-800">{student.name}</span>
                      <button
                        type="button"
                        data-testid={`attendance-toggle-${student.user_id}`}
                        onClick={() => toggle(student.user_id)}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          status === 'present'
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {status === 'present'
                          ? <CheckCircle className="h-4 w-4" />
                          : <XCircle className="h-4 w-4" />}
                        {status === 'present' ? 'Присъства' : 'Отсъства'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  data-testid="attendance-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#00966E] hover:bg-[#007a59] text-white"
                >
                  {saving ? 'Записване...' : 'Запази присъствията'}
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* MONTHLY REPORT */}
      {view === 'report' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Клас</Label>
              <Select value={reportClass} onValueChange={setReportClass} data-testid="report-class-select">
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Изберете клас..." />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Месец</Label>
              <Select value={String(reportMonth)} onValueChange={v => setReportMonth(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_BG.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Година</Label>
              <Select value={String(reportYear)} onValueChange={v => setReportYear(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            data-testid="fetch-report-btn"
            onClick={fetchReport}
            disabled={loadingReport}
            className="bg-[#00966E] hover:bg-[#007a59] text-white"
          >
            {loadingReport ? 'Зареждане...' : 'Покажи справка'}
          </Button>

          {reportData && (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border-b border-r px-3 py-2 text-left font-semibold text-slate-700 sticky left-0 bg-slate-100 min-w-[150px]">
                      Ученик
                    </th>
                    {reportData.dates.map(d => (
                      <th key={d} className="border-b border-r px-2 py-2 text-center font-medium text-slate-600 min-w-[56px]">
                        {d.slice(5)}
                      </th>
                    ))}
                    <th className="border-b px-3 py-2 text-center font-semibold text-red-600 bg-red-50 min-w-[60px]">
                      Отс.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.students.map((s, idx) => (
                    <tr key={s.user_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="border-b border-r px-3 py-2 font-medium text-slate-800 sticky left-0"
                        style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        {s.name}
                      </td>
                      {reportData.dates.map(d => {
                        const status = reportData.matrix[s.user_id]?.[d];
                        return (
                          <td key={d} className="border-b border-r px-2 py-2 text-center">
                            {status === 'present' && <span className="text-green-600 font-bold text-base">П</span>}
                            {status === 'absent' && <span className="text-red-600 font-bold text-base">О</span>}
                            {!status && <span className="text-slate-300">–</span>}
                          </td>
                        );
                      })}
                      <td className={`border-b px-3 py-2 text-center font-bold ${s.absences > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {s.absences}
                      </td>
                    </tr>
                  ))}
                  {reportData.students.length === 0 && (
                    <tr>
                      <td colSpan={reportData.dates.length + 2} className="py-10 text-center text-slate-400">
                        Няма данни за избрания период
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
